import * as React from 'react';
import { Component, Fragment } from 'react';
import { action, observable, computed } from 'mobx';
import { observer, inject } from 'mobx-react';
import styled, { withTheme } from 'styled-components';
import { propStyles } from '@kurtosys/ksys-portal-components/build/utils/propStyles';
import Icon from './Icon';
import Checkbox from './Checkbox';
import DownloadControls from './DownloadControls';
import ClickLayer from './ClickLayer';
import { createPortal } from 'react-dom';

const DropDownWrapper = styled<any, any>('div')`
    ${propStyles};
    position: relative;
`;

DropDownWrapper.defaultProps = {
    display: 'inline-flex',
};

const DropDownButton = styled<any, any>('button')`
    ${propStyles};
    outline: none;
    cursor: pointer;
`;

DropDownButton.defaultProps = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    fontSize: 'inherit',
    fontFamily: 'inherit',
    fontWeight: 'inherit',
    textAlign: 'right',
    bgColor: 'transparent',
    borderStyle: 'none',
};

const DocumentDropdown = styled<any, any>('div')`
    ${propStyles};
    top: 100%;
    right: 0;
`;

DocumentDropdown.defaultProps = {
    position: 'absolute',
    display: 'flex',
    flexDirection: 'column',
    flexWrap: 'nowrap',
    minWidth: '160px',
    maxWidth: '400px',
    maxHeight: '240px',
    overflowY: 'auto',
    bgColor: 'white',
    boxShadow: 'sm',
    borderWidth: '1px',
    borderColor: 'gray300',
    borderStyle: 'solid',
    borderRadius: 'sm',
    zIndex: 'dropdown',
};

const DropdownItem = styled<any, any>('div')`
    ${propStyles};
`;

DropdownItem.defaultProps = {
    display: 'flex',
    alignItems: 'center',
    flex: '0 0 auto',
    paddingX: 'md',
    paddingY: 'sm',
    borderWidth: '1px',
    borderColor: 'gray300',
    borderStyle: 'none none solid none',
    hover: {
        bgColor: 'gray100',
    },
};

const checkboxStyleProps = {
    CheckboxChecked: {
        height: '1.4em',
        width: '1.4em',
        marginX: 'xs',
    },
    CheckboxUnchecked: {
        height: '1.4em',
        width: '1.4em',
        marginX: 'xs',
    },
};

@inject('dataStore')
@observer
class DocumentDropDown extends Component<any, any> {
    @observable open = false;

    @observable target;
    @observable content;

    @observable top: string = '100%';
    @observable right: string = '0';

    @action
    toggleDropdown = () => {
        const { dataStore } = this.props;
        this.open = !this.open;
        // calculate context menu position
        if (this.open && dataStore.isMobile) {
            const parentRect = document
                .getElementById('fundcenter-context-menu')
                .getBoundingClientRect();
            const elemRect = this.target.getBoundingClientRect();
            const top = elemRect.top + elemRect.height - parentRect.top;
            // See: https://stackoverflow.com/questions/1248081/get-the-browser-viewport-dimensions-with-javascript#answer-8876069
            const right =
                Math.max(
                    document.documentElement.clientWidth,
                    window.innerWidth || 0
                ) - elemRect.right;
            this.top = `${top}px`;
            this.right = `${right}px`;
        }
    };

    @action
    refTarget = ref => {
        this.target = ref;
    };

    @action
    refContent = ref => {
        this.content = ref;
    };

    @computed
    get menu() {
        const {
            theme,
            dataStore,
            documents,
            showCheckbox,
            documentSelected,
            toggleDownload,
        } = this.props;

        // used for context menu positioning
        const { top, right } = this;

        return (
            <Fragment>
                <ClickLayer
                    onClick={e => {
                        e.stopPropagation();
                        this.toggleDropdown();
                    }}
                />
                <DocumentDropdown
                    innerRef={this.refContent}
                    style={{ top, right }}
                    {...theme.DocumentDropdown}
                >
                    {documents.map((document, index) => (
                        <DropdownItem
                            key={document.clientCode}
                            {...theme.DocumentDropdownItem}
                        >
                            {showCheckbox && (
                                <Checkbox
                                    checked={documentSelected(
                                        document.clientCode
                                    )}
                                    onChange={() => toggleDownload(document)}
                                    theme={{
                                        ...theme,
                                        ...checkboxStyleProps,
                                    }}
                                />
                            )}
                            <DownloadControls
                                onClick={e => {
                                    e.stopPropagation();
                                    dataStore.downloadDocuments([document]);
                                }}
                                label={dataStore.getDocumentDisplayName(
                                    document
                                )}
                                count={documents.length}
                                showCount={false}
                                flex="1 0 0%"
                                theme={{
                                    ...theme,
                                    ...{
                                        DownloadControls:
                                            theme.DocumentDropdownDownloadControls,
                                        DownloadButton: {
                                            color: 'inherit',
                                            flex: '1 0 0%',
                                            justifyContent: 'space-between',
                                            hover: {
                                                color: 'inherit',
                                            },
                                            ...theme.DocumentDropdownDownloadButton,
                                        },
                                        DownloadButtonIcon:
                                            theme.DocumentDropdownDownloadButtonIcon,
                                    },
                                }}
                            />
                        </DropdownItem>
                    ))}
                </DocumentDropdown>
            </Fragment>
        );
    }

    @computed
    get dropDownMenu() {
        const { dataStore } = this.props;
        // Use portal to pull context menu out of swipe tabs in mobile which was causing it to get cut off
        let dropDownMenu;
        if (dataStore.isMobile) {
            const node =
                document && document.getElementById('fundcenter-context-menu');
            dropDownMenu = createPortal(this.menu, node);
        } else {
            dropDownMenu = this.menu;
        }
        return dropDownMenu;
    }

    render() {
        const { theme, children } = this.props;

        return (
            <DropDownWrapper>
                <DropDownButton
                    id={'DropDownButtonRP'}
                    innerRef={this.refTarget}
                    onClick={e => {
                        e.stopPropagation();
                        this.toggleDropdown();
                    }}
                    {...theme.DocumentDropdownButton}
                >
                    {children}
                    <Icon
                        fileName={
                            this.open
                                ? 'DocumentDropdownIconExpanded'
                                : 'DocumentDropdownIcon'
                        }
                        height="1.4em"
                        width="1.4em"
                        {...theme.DocumentDropdownIcon}
                    />
                </DropDownButton>
                {this.open && this.dropDownMenu}
            </DropDownWrapper>
        );
    }
}

export default withTheme(DocumentDropDown) as any;



// WEBPACK FOOTER //
// ./src/Components/DocumentDropDown.tsx